import styled from '@emotion/styled';
import { define, ui, s, select, when } from '@owenscorning/pcb.alpha';
import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { unwrapRef, wrapRef } from '../../../../../data';
import useReference from '../../../../../hooks/use_reference';
import FilterListResults from '../../../../ComponentLibrary/filter-list/oc-filter-list-results';
import TextSearchFilter from '../../../../ComponentLibrary/filter-list/oc-text-search-filter';
import { BasicContent } from '../../../../OC/PageBuilder/BasicContent';
import createSizes from '../../../../OC/PageBuilder/utilities/sizes';
import { Screens } from '../../../../PageBuilder/editing/Viewport';
import { makeT } from '../../../../location/locales';
import wrappedInRouter from '../../../../wrappedInRouter';
import Data from '../../../Data';

const dataType = 'ItemList';

const Sizes = createSizes();

const ItemListWrapper = styled.div`
  display: flex;
`;

const ItemListSet = styled.div`
  margin: 0;
  width: ${({ width }) => `${width.Mobile}px`};
  @media (min-width: ${Screens.Tablet}px) {
    width: ${({ width }) => `${width.Tablet}px`};
  }
  @media (min-width: ${Screens.Desktop}px) {
    width: ${({ width }) => `${width.Desktop}px`};
  }
`;

const MaxWidth = styled.div`
  width: 100%;
  @media (min-width: ${Screens.Desktop}px) {
    padding-left: 50px;
  }
`;

const NoResult = () => <div />;

const NoProductsRoofingResult = ({ zip, filters = {}, appliedFilters = {} }) => {
  const appliedLabels = Object.keys(appliedFilters)
    .map(attribute => filters[attribute]?.displayName)
    .filter(Boolean);

  return (
    <MaxWidth>
      <h2>No results found for postal code {zip}</h2>
      <span>
        {appliedLabels.length
          ? `No products in your area match the filters you applied: ${appliedLabels.join(', ')}.`
          : 'There are no products available in your area.'}
      </span>
    </MaxWidth>
  );
};

const getReferenceWithCurrentLanguage = ref => {
  if (!ref) return null;
  const [type, value, data] = unwrapRef(ref);
  const newRef = wrapRef(
    type,
    {
      parameters: {},
      language: Board.build.language,
      ...value,
    },
    data
  );

  return newRef;
};

const Renderer = wrappedInRouter(
  ({
    copy,
    type: { source = 'data' } = { source: 'data' },
    data,
    settings,
    socialShare,
    searchFilter,
    tabbedContent,
  }) => {
    const size = settings?.containerSize?.selection || 'xl';
    const alignment = settings?.alignment || 'Left';
    const Width = Sizes[size];
    const adapter = Data.for(dataType)[data?.dataset];
    const centerStyle = {
      justifyContent: 'center',
    };
    const [initialLoad, setInitialLoad] = useState(true);
    const urlParams = useParams();
    const [appliedFilters, setAppliedFilters] = useState(urlParams || {});

    // TEMP!  PB-945 - migrate in old doc builders
    if (
      data?.dataset === 'document_builder' &&
      typeof data?.parameters?.document_builder?.itemAttributes === 'undefined'
    ) {
      data.parameters = data.parameters || {};
      data.parameters.document_builder = data.parameters.document_builder || {};
      data.parameters.document_builder.itemAttributes = ['country', 'language', 'document_type'];
    }
    // END TEMP!  PB-945 - migrate in old doc builders

    let reference = data?.reference;
    if (adapter?.settings?.serverSideCalculation && (Object.keys(appliedFilters)?.length > 0 || !initialLoad)) {
      reference = wrapRef('Cms::DataProvider', {
        type: dataType,
        dataset: data.dataset,
        parameters: { ...data.parameters[data.dataset], filters: appliedFilters },
      });
    }

    const updateFilters = filters => {
      setAppliedFilters(filters);
      setInitialLoad(false);
    };

    // TODO: it also seems we could remove this bit, if we can remove the top-level "dataset" and "parameters" and make part
    // of Data/Parameters
    const [type, value] = reference ? unwrapRef(reference) : [];
    if (value?.dataset !== data?.dataset) {
      reference = null;
    }
    let filterScope = useMemo(() => Board.Path.slice(1, 5), []);
    if (tabbedContent) {
      filterScope = [...filterScope, 'tab', tabbedContent.index];
    }
    filterScope = filterScope.join('-');

    const { results, error, loading } = useReference(getReferenceWithCurrentLanguage(reference));
    const view = adapter?.view(results);
    const { Component, filters } = view || {};
    const enableSearch = view && view.enableSearch === undefined && view.searchFields ? true : view?.enableSearch; // set default to true if not defined
    const t = makeT(Board.build.language);
    const modifiedFilters =
      searchFilter && enableSearch
        ? {
            q: {
              component: TextSearchFilter,
              valueType: 'search',
              displayName: t('filter_list.search'),
              fields: view.searchFields,
              options: [],
              serverSide: false,
            },
            ...filters,
          }
        : filters;

    const isProductsRoofing = data?.dataset === 'products_roofing';

    return (
      <ItemListWrapper style={alignment !== 'Left' ? centerStyle : null}>
        <ItemListSet width={Width} size={size}>
          <BasicContent {...copy} />
          <UI.Row>
            {view && Component && (
              <FilterListResults
                resultComponent={props => (
                  <Component aspectRatio={settings?.aspectRatio} socialShare={socialShare?.image} {...props} />
                )}
                noResultComponent={isProductsRoofing ? NoProductsRoofingResult : NoResult}
                {...{ ...view, filters: modifiedFilters }}
                loading={loading}
                {...adapter?.settings?.resultComponent}
                serverSideCalculation={adapter?.settings?.serverSideCalculation}
                serverSideCalculationUpdate={updateFilters}
                search={{ enable: searchFilter, position: 'left' }}
                pagination={{ perPage: 50 }}
                filterChipsVisible
                allowSearch={isProductsRoofing}
                filterScope={filterScope}
                withSecondTier={isProductsRoofing}
              />
            )}
          </UI.Row>
        </ItemListSet>
      </ItemListWrapper>
    );
  }
);

export default define`Item List`('0.0.1')({
  thumbnail: ui`Modules/Page/Thumbnails/ItemList`,
  category: ui`Modules/Categories/Page/Customizable`,
  view: ({ value }) => <Renderer key={value?.data?.dataset} {...value} />,
  edit: {
    copy: ui`Content/Basic`,
    type: ui`Form`.of({
      source: ui`Choices`.of({
        data: 'Data Driven',
      })({
        label: 'Select Type',
        default: 'data',
        mode: ui`Choices/Mode/Dropdown`,
      }),
      [s._]: ui`Tip`.of('Select the type of items you would like to create or pull data from.'),
    })({
      label: 'Select Type',
    }),
    data: ui`Form`.of({
      dataset: ui`Data/Choices`.of(dataType),
      parameters: ui`Data/Parameters`.of(dataType)({ dataset: select`../dataset` }),
      reference: ui`Data/Reference`({
        type: dataType,
        dataset: select`../dataset`,
        parameters: select`../parameters`,
      }),
    })({
      label: 'Select Details',
      visible: when`../type/source`.is.equal.to('data').then(true).otherwise(false),
    }),
    socialShare: ui`Form`.of({
      image: ui`Switch`({
        default: false,
      }),
      [s._]: ui`Tip`.of(
        "By enabling image social share we will be showing share options on this module's modal images."
      ),
    })({
      label: 'Image Social Sharing',
      visible: when`../data/dataset`.is.equal.to('shingles').then(true).otherwise(false),
    }),
    searchFilter: ui`Switch`({
      label: 'Search Filter',
      default: false,
      visible: when`../data/dataset`.is.equal.to('bim').then(false).otherwise(true),
    }),
    settings: ui`Form`.of({
      aspectRatio: ui`AspectRatio`({
        default: { selection: '1' },
        visible: when`../../data/dataset`.is.equal.to('products_fleetwood').then(false).otherwise(true),
      }),
      alignment: ui`Choices`.of(['Left', 'Center'])({
        label: 'Alignment',
        default: 'Left',
      }),
      containerSize: ui`Form`.of({
        selection: ui`Choices`.of({
          xl: 'XL',
          l: 'Large',
          m: 'Medium',
        })({
          label: 'Container Size',
          default: 'xl',
          mode: ui`Choices/Mode/Dropdown`,
        }),
      }),
    })({
      label: 'Layout',
    }),
  },
});
